/* KCI Event Tagging › Manage Events (KCI_API_DOC §5, mockup Manage Events).
   Filterable events table (server-paginated) + per-event detail dialog carrying
   Send History and the lifecycle actions: Extend / Close / Resume-early / Resend /
   Re-tag, plus per-event CSV exports. */
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Grid, Stack, Typography, TextField, Button, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, CircularProgress, Alert,
} from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import DateRangePicker from '../../components/DateRangePicker'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import TaggedOrdersModal from './TaggedOrdersModal'
import { apiConstants, getDownload } from '../../common/constants'
import { FilterCard, FilterCardContent, FilterRow, FilterField, FieldLabel, FilterActions, ApplyButton, ResetButton } from '../../common/FilterBar'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import {
  useSearchEventsQuery, useEventNotificationsQuery, useEventActionMutation,
  useResendEventMutation, useRetagEventMutation, useActivateEventMutation,
} from '../../redux/kciApiSlice'
import { REASON_CATEGORIES, directionShort, STATUS_COLOR, reasonLabel, reasonColor } from './kciConstants'
import { useForm } from 'react-hook-form'
import { FormProvider, RHFSearchableSelectField, RHFMultipleSelectField } from '../../common/hook-form'
import { NAVY, gridSx, gridWrapSx, CellLine, CellStack, softBtnSx, MUTED, errMsg, COURIER_LIST_FILTER, dirTone, Pill, kciCourierParents, kciServiceTypes, kciAccountOptions, CoverageCell, toKey, parseKey } from './kciUi'

// ── dialog UI helpers ─────────────────────────────────────────────────────────
const FONT = 'Poppins, sans-serif' // admin-panel base font; raw <table>/<td> don't inherit it in a portal
const SummaryItem = ({ k, v }) => (
  <Box>
    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#8A94A6' }}>{k}</Typography>
    <Box sx={{ mt: '3px' }}>{typeof v === 'object' ? v : <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY }}>{v}</Typography>}</Box>
  </Box>
)
const lifecycleBtnSx = (tone) => {
  const c = { blue: { fg: '#1565c0', bg: '#e8f1fc' }, amber: { fg: '#b45309', bg: '#fdf2e5' }, red: { fg: '#c62828', bg: '#fdecea' } }[tone]
  return { textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: '8px', px: 2, py: 0.65, color: c.fg, bgcolor: c.bg, boxShadow: 'none', '&:hover': { bgcolor: c.fg, color: '#fff', boxShadow: 'none' }, '&.Mui-disabled': { opacity: 0.5, boxShadow: 'none' } }
}
const outlineBtnSx = { textTransform: 'none', fontWeight: 600, fontSize: 12.5, borderRadius: '8px', color: '#475569', borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }
const cardSx = { bgcolor: '#fff', border: '1px solid #E7EAF0', borderRadius: '12px', p: 2, mb: 2 }
const audienceTone = (a) => ({ CUSTOMER: { bg: '#e8f1fc', fg: '#1565c0' }, CLIENT: { bg: '#e4f3f1', fg: '#0f766e' }, INTERNAL_OPS: { bg: '#f3edfb', fg: '#6d28d9' } }[a] || { bg: '#eef1f5', fg: '#475569' })
// pretty attachment filename from the S3 url (…/kci_intimations/<uuid>_name.pdf → name.pdf)
const fileName = (u) => { try { return decodeURIComponent((u.split('/').pop() || 'attachment').replace(/^[0-9a-f-]{8,}_/i, '')) } catch { return 'attachment' } }
const uiLabel = { fontSize: 10, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: '#8A94A6' }

// createdBy may arrive as a string, an object ({name/email}), or under an alternate key
const createdByName = (e) => {
  const c = e?.createdBy ?? e?.createdByUser ?? e?.createdByName ?? e?.createdByEmail
  if (c == null || c === '') return '—'
  if (typeof c === 'object') return c.name || c.fullName || c.fullname || c.email || c.userName || '—'
  return c
}
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)
const today = () => new Date().toISOString().slice(0, 10)
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')
// EDD dual-mode readout: "+N days per order" vs a fixed promise date.
const eddLabel = (e) => {
  if (!e.updateCustomEdd) return null
  if (e.eddExtensionDays) return `EDD: +${e.eddExtensionDays} day(s)`
  if (e.revisedEdd) return `EDD: ${fmtDate(e.revisedEdd)}`
  return 'EDD: updated'
}

// Send History is now grouped server-side (one entry per audience, manual actions
// as their own rows, oldest-first). Render entries as-is — no client grouping.
const audienceLabel = (a) => ({ INTERNAL_OPS: 'Internal ops', CLIENT: 'Brand-facing', CUSTOMER: 'End-customer' }[a] || a || '—')
// Recipient summary per privacy rule: addresses only for INTERNAL_OPS (Prozo's own
// lists); brand / customer entries are counts only (PII masked in the DB).
const recipientText = (h) => {
  const n = h.recipientCount ?? 0
  if (h.audience === 'CUSTOMER') return `${n.toLocaleString()} customer${n === 1 ? '' : 's'}`
  if (h.audience === 'CLIENT') return `${n.toLocaleString()} brand contact${n === 1 ? '' : 's'}`
  return `${n.toLocaleString()} recipient${n === 1 ? '' : 's'}`
}
// null → automated; a user + manual flag → "rahul.kumar3 (manual resend)".
const triggeredByText = (h) => {
  if (!h.triggeredBy) return 'System — automated'
  const who = String(h.triggeredBy).split('@')[0]
  return h.manual ? `${who} (manual resend)` : who
}

// ── Send History + actions dialog ─────────────────────────────────────────────
/**
 * "Which accounts does this event actually cover?" — the answer ops could not see.
 * ACCOUNT_LIST events especially: the parent label ("ATS + Baral + Blitz") reads as
 * whole parents even when ATS was narrowed to 2 accounts. Groups the event's
 * resolvedCourierCodes by parent (via the shared courier list) and says, per parent,
 * either "all accounts" or exactly which ones. Codes are de-duplicated for display —
 * duplicate cp_account_codes in courier_partners otherwise repeat here.
 */
function CourierCoverage({ event, couriers }) {
  const resolved = Array.from(new Set(event.resolvedCourierCodes || []))
  if (event.courierScope === 'ALL_COURIERS') {
    return <Typography sx={{ fontSize: 13, color: NAVY }}>All couriers ({resolved.length} accounts)</Typography>
  }
  const parents = (event.parentCouriers?.length ? event.parentCouriers
    : (event.parentCourier || '').split(' + ')).map((p) => p.trim()).filter(Boolean)
  if (!parents.length || !resolved.length) {
    return <Typography sx={{ fontSize: 13, color: NAVY }}>{event.courierCode || '—'}</Typography>
  }
  const familySize = (parent) => couriers.filter((c) => (c.parent || '').trim().toLowerCase() === parent.toLowerCase()).length
  const codesOf = (parent) => {
    const inFamily = new Set(couriers.filter((c) => (c.parent || '').trim().toLowerCase() === parent.toLowerCase()).map((c) => c.cpAccountCode))
    return resolved.filter((code) => inFamily.has(code))
  }
  return (
    <Stack spacing={0.5}>
      {parents.map((parent) => {
        const codes = codesOf(parent)
        const total = familySize(parent)
        const whole = total > 0 && codes.length >= total
        return (
          <Box key={parent} sx={{ fontSize: 12.5, color: NAVY }}>
            <b>{parent}</b>{' — '}
            {whole ? `all ${total} accounts`
              : codes.length ? <Tooltip title={codes.join(', ')}><span>{codes.length} of {total || '?'} accounts: {codes.join(', ')}</span></Tooltip>
                : 'no accounts resolved'}
          </Box>
        )
      })}
    </Stack>
  )
}

function EventDialog({ event, onClose, onChanged }) {
  const { enqueueSnackbar } = useSnackbar()
  // same cached query the list page uses — needed to group coverage codes by parent
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_FILTER)
  const { data: history = [], isLoading } = useEventNotificationsQuery(event.id)
  const [eventAction, { isLoading: acting }] = useEventActionMutation()
  const [resend] = useResendEventMutation()
  const [retag] = useRetagEventMutation()
  const [activate] = useActivateEventMutation()
  const [newEndDate, setNewEndDate] = useState('')
  const [remark, setRemark] = useState('')
  const [confirm, setConfirm] = useState(null) // { tone, title, message, note, confirmLabel, run }
  const [confirmBusy, setConfirmBusy] = useState(false)
  const runConfirm = async () => {
    const fn = confirm?.run
    setConfirmBusy(true)
    try { if (fn) await fn() } finally { setConfirmBusy(false); setConfirm(null) }
  }

  const isDraft = event.status === 'DRAFT'
  const isTerminal = ['CLOSED', 'EXPIRED'].includes(event.status)
  const canRetag = ['ACTIVE', 'EXTENDED'].includes(event.status)

  const doAction = async (action, extra = {}) => {
    try {
      await eventAction({ id: event.id, action, remark: remark || undefined, ...extra }).unwrap()
      enqueueSnackbar(`Event ${action.toLowerCase()} done.`, { variant: 'success' })
      onChanged?.()
    } catch (err) { enqueueSnackbar(errMsg(err, `Could not ${action.toLowerCase()} event.`), { variant: 'error' }) }
  }
  // open the Extend confirmation (validates the date first, then shows the email note)
  const askExtend = () => {
    if (!newEndDate) { enqueueSnackbar('Pick a new end date first.', { variant: 'warning' }); return }
    setConfirm({
      tone: 'blue', title: 'Extend event window', confirmLabel: 'Yes, extend & notify',
      message: `This moves the event's end date to ${fmtDate(newEndDate)}. Shipments that fall in the extended window keep getting KCI-tagged.`,
      note: 'Extending sends a KCI notification email to all affected merchants and to the internal ops team about the revised window. Those emails go out immediately — only extend when you are sure.',
      // plain yyyy-MM-dd — appending a UTC end-of-day time pushed the event a day past the
      // picked date, because the server truncates to end-of-day in IST
      run: () => doAction('EXTEND', { newEndDate }),
    })
  }
  const doResend = async () => {
    try { await resend(event.id).unwrap(); enqueueSnackbar('Client email re-sent.', { variant: 'success' }); onChanged?.() }
    catch (err) { enqueueSnackbar(errMsg(err, 'Resend failed.'), { variant: 'error' }) }
  }
  const doRetag = async () => {
    try { const r = await retag(event.id).unwrap(); enqueueSnackbar(`Re-tag done — ${r?.newlyTagged ?? 0} newly tagged.`, { variant: 'success' }); onChanged?.() }
    catch (err) { enqueueSnackbar(errMsg(err, 'Re-tag failed.'), { variant: 'error' }) }
  }
  const doActivate = async () => {
    try { await activate(event.id).unwrap(); enqueueSnackbar('Draft activated.', { variant: 'success' }); onChanged?.() }
    catch (err) { enqueueSnackbar(errMsg(err, 'Activation failed (overlap?).'), { variant: 'error' }) }
  }
  const doExport = async (kind) => {
    try {
      const res = await getDownload(apiConstants.BASE_LOCAL + `kci/event/${event.id}/export/${kind}`)
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a'); a.href = url; a.download = `kci_${event.id}_${kind}.csv`
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url)
    } catch { enqueueSnackbar('Export failed.', { variant: 'error' }) }
  }

  const edd = eddLabel(event)
  // Subject / Body / Attachment from Step 1 — come back on the event (or its linked
  // intimation); read defensively across the likely shapes.
  const intim = event.intimation || event.sourceIntimation || {}
  const subject = intim.subject || event.subject
  const body = intim.body || event.body
  const attachments = intim.attachmentLinks || event.attachmentLinks || event.attachments || []
  return (
    <>
    <Dialog open onClose={onClose} maxWidth='md' fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.75, fontFamily: FONT, fontWeight: 700, fontSize: 16, color: NAVY }}>
        {event.title}
        <Chip size='small' label={event.status} color={STATUS_COLOR[event.status] || 'default'} sx={{ fontWeight: 700, letterSpacing: '.03em' }} />
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#F7F8FA', fontFamily: FONT }}>
        {/* summary grid — readable at a glance */}
        <Box sx={cardSx}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><SummaryItem k='Event ID' v={<Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: NAVY, wordBreak: 'break-all' }}>{event.id}</Typography>} /></Grid>
            <Grid item xs={12} md={6}><SummaryItem k='Courier coverage' v={<CourierCoverage event={event} couriers={couriers} />} /></Grid>
            <Grid item xs={6} md={3}><SummaryItem k='Direction' v={<Chip size='small' variant='outlined' label={directionShort(event.direction)} />} /></Grid>
            {event.impactType === 'INFORMATIONAL_ONLY' && (
              <Grid item xs={12}>
                <Alert severity='info' sx={{ borderRadius: '10px', py: 0.25 }}>
                  <b>Informational only</b> — logged for the record. This event never tags shipments,
                  applies no revised EDD, and sends no merchant or customer notifications.
                </Alert>
              </Grid>
            )}
            <Grid item xs={6} md={3}><SummaryItem k='Window' v={`${fmtDate(event.startDate)} – ${event.endDate ? fmtDate(event.endDate) : 'until further notice'}`} /></Grid>
            <Grid item xs={12} md={6}><SummaryItem k='Coverage / Pincodes' v={<CoverageCell event={event} />} /></Grid>
            <Grid item xs={6} md={3}><SummaryItem k='Custom EDD' v={edd ? edd.replace('EDD: ', '') : '—'} /></Grid>
            <Grid item xs={6} md={3}><SummaryItem k='Created by' v={createdByName(event)} /></Grid>
          </Grid>
        </Box>

        {/* courier communication — subject / body / attachment from Step 1 */}
        {(subject || body || attachments.length > 0) && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 1.25 }}>Courier communication</Typography>
            {subject && (
              <Box sx={{ mb: (body || attachments.length) ? 1.5 : 0 }}>
                <Typography sx={uiLabel}>Subject</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: NAVY, mt: '2px' }}>{subject}</Typography>
              </Box>
            )}
            {body && (
              <Box sx={{ mb: attachments.length ? 1.5 : 0 }}>
                <Typography sx={uiLabel}>Message</Typography>
                <Box sx={{ mt: 0.5, p: 1.25, borderRadius: '8px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F5' }}>
                  <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{body}</Typography>
                </Box>
              </Box>
            )}
            {attachments.length > 0 && (
              <Box>
                <Typography sx={{ ...uiLabel, mb: 0.5 }}>Attachment{attachments.length > 1 ? 's' : ''}</Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  {attachments.map((u, i) => (
                    <Button key={i} variant='outlined' size='small' sx={outlineBtnSx} startIcon={<AttachFileOutlinedIcon sx={{ fontSize: 15 }} />}
                      component='a' href={u} target='_blank' rel='noopener noreferrer'>{fileName(u)}</Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* actions */}
        {!isTerminal && (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 1.25 }}>Actions</Typography>
            <TextField size='small' label='Remark (stored on Send History)' fullWidth value={remark} onChange={(e) => setRemark(e.target.value)} sx={{ mb: 1.5 }} />
            {isDraft ? (
              <Button variant='contained' sx={lifecycleBtnSx('blue')} onClick={doActivate}>Save &amp; trigger (activate draft)</Button>
            ) : (
              <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap' useFlexGap>
                <TextField size='small' type='date' label='New end date' InputLabelProps={{ shrink: true }} value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} sx={{ width: 170 }} />
                <Button variant='contained' sx={lifecycleBtnSx('blue')} onClick={askExtend} disabled={acting}>Extend</Button>
                <Button variant='contained' sx={lifecycleBtnSx('amber')} disabled={acting} onClick={() => setConfirm({
                  tone: 'amber', title: 'Resume event early', confirmLabel: 'Resume early',
                  message: `This re-opens the event before its scheduled end date, so matching shipments start getting KCI-tagged again from now.`,
                  run: () => doAction('RESUME_EARLY'),
                })}>Resume early</Button>
                <Button variant='contained' sx={lifecycleBtnSx('red')} disabled={acting} onClick={() => setConfirm({
                  tone: 'red', title: 'Close this event', confirmLabel: 'Close event',
                  message: `This ends the event now — shipments stop being KCI-tagged from this point. You can re-open it later with "Resume early".`,
                  run: () => doAction('CLOSE'),
                })}>Close</Button>
                <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
                <Button variant='contained' sx={softBtnSx} onClick={() => setConfirm({
                  tone: 'blue', title: 'Resend client email', confirmLabel: 'Resend',
                  message: `This re-sends the KCI notification email to this event's brand / client contacts.`,
                  run: doResend,
                })}>Resend client email</Button>
                {canRetag && <Button variant='contained' sx={softBtnSx} onClick={() => setConfirm({
                  tone: 'blue', title: 'Run manual re-tag', confirmLabel: 'Re-tag now',
                  message: `This re-scans orders in the event's scope and tags any newly-matching shipments.`,
                  run: doRetag,
                })}>Manual re-tag</Button>}
              </Stack>
            )}
          </Box>
        )}

        {/* exports */}
        <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
          <Button variant='outlined' sx={outlineBtnSx} startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => doExport('awbs')}>Export AWBs</Button>
          <Button variant='outlined' sx={outlineBtnSx} startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => doExport('pincodes')}>Export pincodes</Button>
        </Stack>

        {/* send history */}
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mb: 1 }}>Send History</Typography>
        {isLoading ? <Stack alignItems='center' py={2}><CircularProgress size={20} /></Stack> : (
          <Box sx={{ overflowX: 'auto', bgcolor: '#fff', border: '1px solid #E7EAF0', borderRadius: '10px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: FONT }}>
              <thead><tr style={{ textAlign: 'left', color: '#8A94A6', background: '#F9FAFB' }}>
                {['Sent At', 'Audience', 'Recipients', 'Channel', 'Status', 'Triggered By'].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', fontWeight: 700, fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {history.length === 0 && <tr><td colSpan={6} style={{ padding: 14, color: MUTED, textAlign: 'center' }}>No communications sent yet.</td></tr>}
                {history.map((h, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #EEF1F5', verticalAlign: 'top', background: h.manual ? '#FCFBF7' : undefined }}>
                    <td style={{ padding: '9px 12px', color: NAVY, whiteSpace: 'nowrap' }}>{h.sentAt ? new Date(h.sentAt).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <Box component='span' sx={{ display: 'inline-block', px: 0.9, py: 0.2, borderRadius: '6px', fontSize: 10.5, fontWeight: 700, color: audienceTone(h.audience).fg, bgcolor: audienceTone(h.audience).bg }}>{audienceLabel(h.audience)}</Box>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#475569', maxWidth: 320 }}>
                      <Box sx={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{recipientText(h)}</Box>
                      {/* addresses only for INTERNAL_OPS (Prozo's own lists) */}
                      {h.audience === 'INTERNAL_OPS' && Array.isArray(h.recipients) && h.recipients.length > 0 && (
                        <Box sx={{ fontSize: 11, color: MUTED, mt: '2px', wordBreak: 'break-word' }}>{h.recipients.join(', ')}</Box>
                      )}
                      {h.sends != null && h.sends !== h.recipientCount && (
                        // "sends" is the number of send BATCHES behind this row, not emails —
                        // "1 send" next to "7 customers" read as a contradiction
                        <Box sx={{ fontSize: 11, color: MUTED, mt: '2px' }}>{h.sends} attempt{h.sends === 1 ? '' : 's'}</Box>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#475569' }}>{h.channel}</td>
                    <td style={{ padding: '9px 12px' }}><Chip size='small' label={h.status} color={h.status === 'FAILED' ? 'error' : h.status === 'RESENT' ? 'info' : 'success'} sx={{ fontWeight: 700, height: 20 }} /></td>
                    <td style={{ padding: '9px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{triggeredByText(h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}><Button onClick={onClose} sx={outlineBtnSx} variant='outlined'>Close</Button></DialogActions>
    </Dialog>

    {confirm && (
      <Dialog open onClose={() => !confirmBusy && setConfirm(null)} maxWidth='xs' fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 15, color: NAVY, pb: 1 }}>{confirm.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>{confirm.message}</Typography>
          {confirm.note && (
            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, p: 1.25, borderRadius: '10px', bgcolor: '#FFF7E6', border: '1px solid #F5C97B' }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 18, color: '#B45309', mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12, color: '#7A4A00', fontWeight: 600, lineHeight: 1.5 }}>{confirm.note}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirm(null)} disabled={confirmBusy} sx={outlineBtnSx} variant='outlined'>Cancel</Button>
          <Button onClick={runConfirm} disabled={confirmBusy} variant='contained' sx={lifecycleBtnSx(confirm.tone || 'blue')}>{confirmBusy ? 'Working…' : (confirm.confirmLabel || 'Confirm')}</Button>
        </DialogActions>
      </Dialog>
    )}
    </>
  )
}

export default function ManageEvents() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_FILTER)
  // dropdown drafts live in react-hook-form (shared RHFSearchableSelectField);
  // dates stay local. `applied` is what actually drives the API (set on "Filter").
  const methods = useForm({ defaultValues: { status: '', courierParent: '', courierServiceType: [], courierCode: '', reasonCategory: '' } })
  const { handleSubmit } = methods
  const courierParent = methods.watch('courierParent')
  const courierServiceType = methods.watch('courierServiceType')
  const courierCode = methods.watch('courierCode')
  const [from, setFrom] = useState(daysAgo(30))
  const [to, setTo] = useState(today())
  const [applied, setApplied] = useState({ status: '', courierCode: '', reasonCategory: '', from: daysAgo(30), to: today() })
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [selected, setSelected] = useState(null)
  const [taggedFor, setTaggedFor] = useState(null)

  const applyFilters = (data) => { setApplied({ status: data.status, courierCode: data.courierCode, reasonCategory: data.reasonCategory, from, to }); setPage(0) }
  const resetFilters = () => {
    const d = daysAgo(30), t = today()
    methods.reset({ status: '', courierParent: '', courierServiceType: [], courierCode: '', reasonCategory: '' })
    setFrom(d); setTo(t)
    setApplied({ status: '', courierCode: '', reasonCategory: '', from: d, to: t }); setPage(0)
  }

  const params = useMemo(() => {
    const p = { from: applied.from, to: applied.to, page: page + 1, size: sizePerPage }
    if (applied.status) p.status = applied.status
    if (applied.courierCode) p.courierCode = applied.courierCode
    if (applied.reasonCategory) p.reasonCategory = applied.reasonCategory
    return p
  }, [applied, page, sizePerPage])

  const { data, isFetching, isError, error, refetch } = useSearchEventsQuery(params)
  const rows = data?.events || []
  const rowCount = data?.totalRecords ?? rows.length

  useEffect(() => {
    if (isError) enqueueSnackbar(errMsg(error, 'Could not load KCI events.'), { variant: 'error' })
  }, [isError, error, enqueueSnackbar])

  const columns = [
    {
      field: 'title', headerName: 'Event', minWidth: 220, flex: 1.4, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold>{p.row.title || '—'}</CellLine>
          <CellLine muted ellipsis>{p.row.id}</CellLine>
        </CellStack>
      ),
    },
    {
      field: 'courier', headerName: 'Courier', minWidth: 150, flex: 1, sortable: false,
      // compact coverage: scope + real account count, full code list (capped) on hover —
      // the full per-parent breakdown lives in the Details dialog
      renderCell: (p) => {
        const codes = Array.from(new Set(p.row.resolvedCourierCodes || []))
        const scopeText = p.row.courierScope === 'ALL_COURIERS' ? 'All couriers'
          : p.row.courierScope === 'ACCOUNT_LIST' ? 'Selected accounts'
            : p.row.courierScope === 'PARENT_WIDE' ? 'Parent-wide' : 'Account'
        const shown = codes.slice(0, 30)
        const tip = shown.join(', ') + (codes.length > shown.length ? ` … +${codes.length - shown.length} more` : '')
        return (
          <Tooltip title={tip}>
            <Box sx={{ minWidth: 0 }}>
              <CellStack>
                <CellLine ellipsis>{p.row.parentCourier || p.row.courierCode || '—'}</CellLine>
                <CellLine muted>{scopeText} · {codes.length} account{codes.length === 1 ? '' : 's'}</CellLine>
              </CellStack>
            </Box>
          </Tooltip>
        )
      },
    },
    { field: 'direction', headerName: 'Direction', minWidth: 110, sortable: false, renderCell: (p) => <Pill tone={dirTone(p.row.direction)} dot>{directionShort(p.row.direction)}</Pill> },
    { field: 'coverage', headerName: 'Coverage / Pincodes', minWidth: 180, flex: 1.1, sortable: false, renderCell: (p) => <CoverageCell event={p.row} /> },
    {
      field: 'reason', headerName: 'Reason', minWidth: 150, flex: 1, sortable: false,
      renderCell: (p) => (p.row.reasonCategory
        ? <Pill tone={{ bg: `${reasonColor(p.row.reasonCategory)}22`, fg: NAVY }} dot dotColor={reasonColor(p.row.reasonCategory)}>{reasonLabel(p.row.reasonCategory)}</Pill>
        : <CellLine muted>—</CellLine>),
    },
    { field: 'createdBy', headerName: 'Created By', minWidth: 120, sortable: false, renderCell: (p) => <CellLine muted>{createdByName(p.row)}</CellLine> },
    {
      field: 'dates', headerName: 'Start–End', minWidth: 130, sortable: false,
      renderCell: (p) => (
        <CellStack>
          <CellLine bold>{fmtDate(p.row.startDate)}</CellLine>
          <CellLine muted>→ {p.row.endDate ? fmtDate(p.row.endDate) : 'UFN'}</CellLine>
        </CellStack>
      ),
    },
    { field: 'status', headerName: 'Status', minWidth: 130, sortable: false, renderCell: (p) => (
      <Stack spacing={0.4} alignItems='flex-start'>
        <Tooltip title={(p.row.status || '').replace(/_/g, ' ')}>
          <Chip size='small' label={p.row.status} color={STATUS_COLOR[p.row.status] || 'default'} sx={{ fontWeight: 700, height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.8 } }} />
        </Tooltip>
        {/* without this an informational event is indistinguishable from a broken one — both
            sit at 0 orders tagged forever */}
        {p.row.impactType === 'INFORMATIONAL_ONLY' && (
          <Tooltip title='Informational only — logged for the record; never tags shipments, no merchant or customer notifications.'>
            <Chip size='small' variant='outlined' label='Informational' sx={{ height: 18, fontSize: 9.5, fontWeight: 700, color: MUTED, borderColor: '#CBD5E1', '& .MuiChip-label': { px: 0.7 } }} />
          </Tooltip>
        )}
      </Stack>
    ) },
    {
      field: 'taggedOrderCount', headerName: 'Orders tagged', description: 'Shipments auto-attached to this event (matched its courier, geography & date window) — their delay is attributed to this disruption and excluded from SLA.', minWidth: 120, align: 'left', headerAlign: 'left', sortable: false,
      renderCell: (p) => {
        const n = p.row.taggedOrderCount ?? 0
        const pill = <Pill tone={n > 0 ? { bg: '#f3edfb', fg: '#6d28d9' } : { bg: '#eef1f5', fg: '#8A94A6' }}>{n.toLocaleString()}</Pill>
        return n > 0 ? <Box component='span' title='View tagged orders' sx={{ cursor: 'pointer' }} onClick={() => setTaggedFor(p.row)}>{pill}</Box> : pill
      },
    },
    { field: 'actions', headerName: 'Actions', minWidth: 120, sortable: false, renderCell: (p) => <Button size='small' sx={softBtnSx} onClick={() => setSelected(p.row)}>Details</Button> },
  ]

  return (
    <Box>
      <PageTitle>KCI Management · Manage Events</PageTitle>

      <FilterCard>
        <FilterCardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
            <FilterRow>
              <FilterField>
                <FieldLabel>Status</FieldLabel>
                <RHFSearchableSelectField name='status' label='' fullWidth placeholder='All statuses'
                  options={['DRAFT', 'SCHEDULED', 'ACTIVE', 'EXTENDED', 'CLOSED', 'EXPIRED'].map((s) => ({ value: s, label: s }))} />
              </FilterField>
              <FilterField>
                <FieldLabel>Courier</FieldLabel>
                <RHFSearchableSelectField name='courierParent' label='' fullWidth placeholder='All couriers'
                  options={kciCourierParents(couriers)} />
              </FilterField>
              {courierParent && (
                <FilterField>
                  <FieldLabel>Service Type</FieldLabel>
                  <RHFMultipleSelectField name='courierServiceType' label='' width='100%' search outputFormat='array'
                    placeholder='All service types' options={kciServiceTypes(couriers, courierParent)} />
                </FilterField>
              )}
              {courierParent && Array.isArray(courierServiceType) && courierServiceType.length > 0 && (
                <FilterField>
                  <FieldLabel>Account</FieldLabel>
                  <RHFSearchableSelectField name='courierCode' label='' fullWidth placeholder='All accounts'
                    options={(() => {
                      const opts = kciAccountOptions(couriers, courierParent, courierServiceType)
                      if (courierCode && !opts.some((o) => o.value === courierCode)) {
                        const c = couriers.find((x) => x.cpAccountCode === courierCode)
                        opts.push({ value: courierCode, label: c && c.name ? `${c.name} (${courierCode})` : courierCode })
                      }
                      return opts
                    })()} />
                </FilterField>
              )}
              <FilterField>
                <FieldLabel>Reason</FieldLabel>
                <RHFSearchableSelectField name='reasonCategory' label='' fullWidth placeholder='All reasons'
                  options={REASON_CATEGORIES.map((r) => ({ value: r.value, label: r.label }))} />
              </FilterField>
              <FilterField sx={{ flex: '0 0 280px' }}>
                <FieldLabel>Date range</FieldLabel>
                <DateRangePicker showTextBox size='small' label='' placeholder='Select date range' maxDate={new Date()}
                  defaultRange={from && to ? [{ startDate: parseKey(from), endDate: parseKey(to), key: 'selection' }] : []}
                  onChange={(range) => {
                    if (range?.length && range[0].startDate && range[0].endDate) {
                      setFrom(toKey(range[0].startDate)); setTo(toKey(range[0].endDate))
                    }
                  }} />
              </FilterField>
              <FilterActions>
                <ApplyButton type='submit' variant='contained' size='small'>Filter</ApplyButton>
                <ResetButton type='button' variant='contained' size='small' onClick={resetFilters}>Reset</ResetButton>
              </FilterActions>
            </FilterRow>
          </FormProvider>
        </FilterCardContent>
      </FilterCard>

      <Box sx={gridWrapSx}>
        <ProzoDataGrid
          columns={columns} rows={rows} getRowId={(r) => r.id} rowCount={rowCount}
          paginationMode='server' page={page} setPage={setPage}
          sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
          loading={isFetching} getRowHeight={() => 'auto'} sx={gridSx}
        />
      </Box>

      {selected && <EventDialog event={selected} onClose={() => setSelected(null)} onChanged={() => { refetch(); setSelected(null) }} />}
      {taggedFor && <TaggedOrdersModal event={taggedFor} onClose={() => setTaggedFor(null)} />}
    </Box>
  )
}
